"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { useAuthenticatedContent } from "../../../hooks/useAuthenticatedContent";
import useDeviceInfo from "../../../hooks/useDeviceInfo";
import ConnectWallet from "../../common/ConnectWallet";
import UserSetUpProfileCta from "../../user/utils/set-up-profile/UserSetUpProfileCta";
import WavesDesktop from "../WavesDesktop";
import WavesMobile from "../WavesMobile";
import PublicWaveShell from "../public/PublicWaveShell";
import { type PublicWaveShellState, usePublicWaveShellState } from "../public/usePublicWaveShellState";
function getConnectPrompt(contentState: ReturnType<typeof useAuthenticatedContent>["contentState"]): ReactNode {
  switch (contentState) {
    case "needs-profile":
      return <>
          <h1 className="tw-text-xl tw-font-bold">
            You need to set up a profile to continue.
          </h1>
          <UserSetUpProfileCta />
        </>;
    case "not-available":
      return <h1 className="tw-text-xl tw-font-bold">
          This content is not available.
        </h1>;
    case "loading":
    case "measuring":
    case "not-authenticated":
    case "ready":
    default:
      return null;
  }
}
function getNotAuthenticatedContent({
  activeWaveId,
  containerClassName,
  isApp,
  publicWaveShellState
}: {
  readonly activeWaveId: string | null;
  readonly containerClassName: string;
  readonly isApp: boolean;
  readonly publicWaveShellState: PublicWaveShellState;
}): ReactNode {
  if (isApp) {
    return <ConnectWallet />;
  }
  if (activeWaveId === null || publicWaveShellState.status === "unavailable") {
    return <ConnectWallet />;
  }
  const publicShell = <div className={containerClassName}>
      <PublicWaveShell waveId={activeWaveId} />
    </div>;
  return <div className="tw-flex-1" id="waves-content">
      <WavesDesktop allowDropOverlay={false} allowRightSidebar={false} showLeftSidebar={true}>
        {publicShell}
      </WavesDesktop>
    </div>;
}

// Main layout content that uses the Layout context
function WavesLayoutContent(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const {
    contentState
  } = useAuthenticatedContent();
  const {
    isApp
  } = useDeviceInfo();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    });
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeWaveId = t1;
  const t2 = !isApp && contentState === "not-authenticated" && activeWaveId !== null;
  let t3;
  if ($[3] !== t2) {
    t3 = {
      enabled: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const publicWaveShellState = usePublicWaveShellState(activeWaveId, t3);
  let t4;
  if ($[5] !== contentState) {
    t4 = getConnectPrompt(contentState);
    $[5] = contentState;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const connectPrompt = t4;
  let content;
  if (contentState === "ready") {
    const Component = isApp ? WavesMobile : WavesDesktop;
    let t5;
    if ($[7] !== children) {
      t5 = <div className="tw-relative tw-flex tw-flex-col tw-flex-1 tailwind-scope">{children}</div>;
      $[7] = children;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== Component || $[10] !== t5) {
      t6 = <div className="tw-flex-1" id="waves-content"><Component>{t5}</Component></div>;
      $[9] = Component;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    content = t6;
  } else {
    if (contentState === "not-authenticated") {
      let t5;
      if ($[12] !== activeWaveId || $[13] !== isApp || $[14] !== publicWaveShellState) {
        t5 = getNotAuthenticatedContent({
          activeWaveId,
          containerClassName: "tw-relative tw-flex tw-flex-col tw-flex-1 tailwind-scope",
          isApp,
          publicWaveShellState
        });
        $[12] = activeWaveId;
        $[13] = isApp;
        $[14] = publicWaveShellState;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      content = t5;
    } else {
      let t5;
      if ($[16] !== connectPrompt) {
        t5 = connectPrompt === null ? null : <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-px-6"><div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center">{connectPrompt}</div></div>;
        $[16] = connectPrompt;
        $[17] = t5;
      } else {
        t5 = $[17];
      }
      content = t5;
    }
  }
  let t5;
  if ($[18] !== content) {
    t5 = <div className="tailwind-scope tw-flex tw-flex-col tw-overflow-hidden tw-bg-black">{content}</div>;
    $[18] = content;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
export default function WavesLayout(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <WavesLayoutContent>{children}</WavesLayoutContent>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}